/**
 * Feature cards - Figma `6377:88602`.
 *
 * A lead paragraph over a row of **square** cards, each a `grey-200` panel with the
 * product's name on a white chip in the top-left corner and a product screenshot that
 * overflows the card and is clipped by it. On the Online Tools page these are Cytric and
 * MY-WCP.
 *
 * **The caveat this file used to carry is resolved.** The inventory grouped two nodes
 * under `feature-cards` and they really are different sections - but the other one,
 * `6377:88784`, is a photograph with feature pills over it, and it now has its own type
 * (`photo-highlights`). This renders `6377:88602` and nothing else.
 *
 * The cards carry decorative gradient polygons behind the screenshot. Sampling the file's
 * own render says they land within two or three values of `grey-200` - #f3f5fb at the
 * very corner against #f1f1f1 elsewhere - so a flat `surface-sunken` panel is a faithful
 * rendering. Per-card gradient art would be inventing a field nobody has asked for.
 *
 * `card.label` is the chip and `card.image` the screenshot. **`card.title` is not drawn**:
 * the design puts no words on the card beyond the chip, and the two would be the same
 * name twice. It is required by `shared.card`, so it is used where a name is genuinely
 * needed - the image's description, and the link's accessible name. `card.body` renders
 * when an editor writes one, which is the only way to put a sentence on the card, and
 * `card.url` makes the whole panel a link.
 */
import { SectionShell } from '../primitives';
import { Image, SIZES } from '../../components/ui/Image';
import type { Card, FeatureCardsSection } from '../../types/sections';
import { AppLink } from '../../components/ui/AppLink';

function Panel({ card }: { card: Card }) {
  const inner = (
    <>
      {card.image ? (
        <Image
          media={card.image}
          alt={card.image.alternativeText ?? card.label ?? card.title ?? ''}
          sizes={SIZES.half}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}

      <div className="relative flex h-full flex-col gap-5">
        {/* 110 x 38 inset 22px from the corner in the file; a pill of text here, so it
            is sized by its content rather than pinned to a width a longer name breaks. */}
        {card.label ? (
          <span className="inline-flex w-fit rounded-full bg-surface px-5 py-2 text-body-sm text-ink">
            {card.label}
          </span>
        ) : null}

        {card.body ? (
          <p className="mt-auto max-w-md text-body text-ink-strong">{card.body}</p>
        ) : null}
      </div>
    </>
  );

  const className = 'group relative aspect-square overflow-hidden rounded-l bg-surface-sunken p-5';
  return card.url
    ? <AppLink to={card.url} aria-label={card.title} className={className}>{inner}</AppLink>
    : <article aria-label={card.title} className={className}>{inner}</article>;
}

export default function FeatureCards({ data }: { data: FeatureCardsSection }) {
  return (
    <SectionShell settings={data.settings} name="feature-cards">
      {data.heading ? <h2 className="max-w-4xl text-h3 text-ink lg:text-h2">{data.heading}</h2> : null}
      {/* 968 of the 1360 grid, at h4 - 3883:71096 sets it at 26px, not the 20px this
          used to draw, and the file leaves 120px between it and the cards. */}
      {data.lead ? <p className="max-w-[71%] whitespace-pre-line text-h4 text-ink-strong">{data.lead}</p> : null}

      {data.cards?.length ? (
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:mt-[120px]">
          {data.cards.map((card, i) => <Panel key={i} card={card} />)}
        </div>
      ) : null}
    </SectionShell>
  );
}
